<template>
    <div>
        <!-- Organization Selector Header -->
        <div class="mb-4 md:mb-8 flex justify-end relative z-[100]">
            <div v-if="myOrgs.length > 0" class="relative inline-block text-left">
                
                <!-- CASE 1: Multiple Organizations (Dropdown) -->
                <div v-if="myOrgs.length > 1" class="dropdown-container">
                    <button 
                        @click="isDropdownOpen = !isDropdownOpen"
                        class="org-dropdown-trigger flex items-center justify-between px-4 py-2 bg-white rounded-lg border border-gray-100 shadow-sm hover:shadow-md transition-all duration-200 min-w-[240px]"
                    >
                        <div class="flex items-center gap-3">
                            <div class="org-avatar-mini">
                                <img v-if="orgData.logo" :src="constants.s3Bucket + '/organization/logo/' + orgData.logo" class="w-8 h-8 rounded-full object-cover shadow-inner">
                                <div v-else class="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-xs">
                                    {{ orgData.name ? orgData.name.charAt(0) : 'O' }}
                                </div>
                            </div>
                            <div class="text-left">
                                <p class="text-sm font-semibold text-gray-800 leading-none mb-1">{{ orgData.name || 'Select Organization' }}</p>
                                <p class="text-[10px] text-gray-400 uppercase tracking-wider font-medium">Current Organization</p>
                            </div>
                        </div>
                            <i class="vs-icon notranslate icon-scale material-icons ml-2 text-gray-500">
                            expand_more
                            </i>
                        </button>

                    <!-- Dropdown Menu -->
                    <transition name="fade-slide">
                        <div v-if="isDropdownOpen" class="absolute right-0 mt-2 org-dropdown-menu">
                        <div class="px-4 py-3 bg-gray-50/50 border-b border-gray-50">
                                <span class="text-xs font-bold text-gray-500 uppercase flex items-center gap-2">
                                     My Organizations
                                    <span class="org-count-circle ml-auto">
                                    {{ myOrgs.length }}
                                    </span>
                                </span>
                            </div>
                            <div class="max-h-64 overflow-y-auto py-1">
                                <div 
                                    v-for="org in myOrgs" 
                                    :key="org._id"
                                    @click="handleOrgSelect(org)"
                                    class="org-item"
                                    :class="{'bg-blue-50/30': org._id === orgData._id}"
                                >
                                    <div class="w-8 h-8 rounded-full overflow-hidden border border-gray-100 bg-white">
                                        <img v-if="org.logo" :src="constants.s3Bucket + '/organization/logo/' + org.logo" class="w-full h-full object-cover">
                                        <div v-else class="w-full h-full flex items-center justify-center bg-gray-100 text-gray-400 text-xs uppercase">{{ org.name.charAt(0) }}</div>
                                    </div>
                                    <span class="text-sm font-medium text-gray-700 flex-1 group-hover:text-blue-600 transition-colors">
                                        {{ org.name }}
                                    </span>
                                    <i
                                    v-if="org._id === orgData._id"
                                    class="vs-icon notranslate icon-scale material-icons ml-auto text-primary text-sm"
                                    >
                                    check
                                    </i>
                                </div>
                            </div>
                        </div>
                    </transition>
                </div>

                <!-- CASE 2: Single Organization (Static View) -->
                <div v-else class="flex items-center gap-3 px-5 py-3 bg-white rounded-lg border border-gray-100 shadow-sm">
                    <div class="org-avatar-mini">
                        <img v-if="orgData.logo" :src="constants.s3Bucket + '/organization/logo/' + orgData.logo" class="w-10 h-10 rounded-full object-cover">
                        <div v-else class="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-500 font-bold">{{ orgData.name.charAt(0) }}</div>
                    </div>
                    <div class="text-left">
                        <p class="text-base font-bold text-gray-800 leading-tight">{{ orgData.name }}</p>
                        <p class="text-[11px] text-gray-400 uppercase font-semibold">Current Organization</p>
                    </div>
                </div>
            </div>
        </div>

        <div class="relative z-1">
            <div id="organisationChartBody" class="organisationChartBody read-only-chart" ref="organiGramBox">
            </div>
        </div>

        <!-- Hidden component for the Infographic Profile -->
        <talent-card-preview 
            ref="otherUserCard"
            :userId="selectedOtherUserId" 
            :initialData="selectedOtherUserData"
        />
    </div>
</template>

<script>
import constants from "@/constants";
import { OrganiGram as OrganiGramChart } from "../../../org-dashboard/users/components/OrganiGram/src/organi-gram";
import skills from "@/assets/data/all-skills.js";
import softSkills from "@/assets/data/all-personality.js";
import TalentCardPreview from "@/views/org-dashboard/talent-pool/components/TalentCardPreview.vue";
// We no longer strictly depend on the hardcoded file, but we keep it as a final fallback
import { kvdrOrgConfig } from "@/assets/data/kvdrOrgConfigs.js";

export default {
    props: { 'importData': Array },
    name: 'OrganiGram',
    components: { TalentCardPreview },
    data() {
        return {
            chartObject: null,
            candidateList: null,
            dataCollection: '',
            kvdrId: '',
            myOrgs: [],
            orgData: {},
            userRole: '1',
            orgEmployees: [],
            accessLevel: [],
            isUserView: false,
            selectedOtherUserId: '',
            selectedOtherUserData: {},
            isDropdownOpen: false, 
            constants,
            
            // --- NEW: Store the dynamic config here ---
            dynamicKvdrConfig: null 
        }
    },
    computed: {
        userId() { return this.$store.state.auth.user._id; },
        userData() { return this.$store.state.users.data; },
        employeesData() { return this.$store.state.organization.employees; },
    },
    methods: {
        // --- NEW METHOD: Fetch Dynamic Config ---
        async fetchOrgConfig(org) {
            let finalConfig = {};
            try {
                const orgId = org._id;
                const token = localStorage.getItem('feathers-jwt') || localStorage.getItem('accessToken');
                const baseUrl = process.env.VUE_APP_API_URL || 'https://api.getboarded.tech';

                const response = await fetch(`${baseUrl}/kvdr-org-config?orgId=${orgId}`, {
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
                });
                const json = await response.json();

                if (json.data && json.data.length > 0) {
                    finalConfig = json.data[0];
                }
            } catch (err) {
                console.error("Error fetching config:", err);
            }

            // LocalStorage Override (If API gives default or fails)
            if (!finalConfig.orgId || finalConfig.orgId === "000000000000000000000000" || finalConfig.orgId === "0") {
                try {
                    const slug = org.slug;
                    if (slug) {
                        const localDataStr = localStorage.getItem(`onboardingConfigs_${slug}`);
                        if (localDataStr) {
                            const localData = JSON.parse(localDataStr);
                            if (localData.appraisal) finalConfig.appraisalData = localData.appraisal;
                            if (localData.fitment) finalConfig.fitmentData = localData.fitment;
                            if (localData.succession) finalConfig.successionData = localData.succession;
                            if (localData.performance) finalConfig.performanceData = localData.performance;
                        }
                    }
                } catch (e) {
                    console.warn("Could not load local dynamic config", e);
                }
            }

            // Fallback mappings
            if (!finalConfig.appraisalData && finalConfig.appraisal) finalConfig.appraisalData = finalConfig.appraisal;
            if (!finalConfig.fitmentData && finalConfig.fitment) finalConfig.fitmentData = finalConfig.fitment;

            // If completely empty, fallback to the hardcoded file
            if (Object.keys(finalConfig).length === 0) {
                 finalConfig = kvdrOrgConfig(org._id);
            }

            // Ensure critical arrays (like accessLevel and calculationTypes) exist
            if (!finalConfig.accessLevel || !finalConfig.calculationTypes) {
                 const fallback = kvdrOrgConfig(org._id);
                 if (!finalConfig.accessLevel) finalConfig.accessLevel = fallback.accessLevel;
                 if (!finalConfig.calculationTypes) finalConfig.calculationTypes = fallback.calculationTypes;
            }

            this.dynamicKvdrConfig = finalConfig;
        },

        handleOrgSelect(org) {
            localStorage.setItem('preferred_org_selection', JSON.stringify({
                id: org._id,
                timestamp: Date.now()
            }));
            this.selectOrganization(org);
            this.isDropdownOpen = false;
        },

        async initializeOrgs() {
    console.log("🚀 [Orgs Init] Starting combined organization fetch...");
    
    try {
        const userEmail = this.$store.state.auth.user.email;
        const currentUserId = this.userId;
        const token = localStorage.getItem('feathers-jwt') || localStorage.getItem('accessToken');
        
        // Using the URL from your Postman screenshot
        const platformBaseUrl = 'https://platform.getboarded.com'; 

        console.log(`🔍 [Orgs Init] Context: Email -> ${userEmail}, UserID -> ${currentUserId}`);

        // 1. Trigger both requests in parallel to save time
        const [storeResponse, apiResponse] = await Promise.allSettled([
            this.$store.dispatch("organization/fetchOrganizationByUserId", currentUserId),
            fetch(`${platformBaseUrl}/api/employees/email/${encodeURIComponent(userEmail)}`, {
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json' 
                }
            }).then(res => res.json())
        ]);

        let allFoundOrgs = [];

        // 2. Process Data from Vuex Store
        if (storeResponse.status === 'fulfilled' && storeResponse.value?.data?.data) {
            const storeOrgs = storeResponse.value.data.data;
            console.log("📦 [Orgs Init] Received from Store API:", storeOrgs);
            allFoundOrgs = [...storeOrgs];
        } else {
            console.warn("⚠️ [Orgs Init] Store API empty or failed:", storeResponse.reason);
        }

        // 3. Process Data from Platform Service (The new Email API)
        if (apiResponse.status === 'fulfilled' && Array.isArray(apiResponse.value)) {
            console.log("🌐 [Orgs Init] Received from Platform Email API:", apiResponse.value);
            
            // We map 'organizationData' to the top level so it matches your component's expectations
            const emailOrgs = apiResponse.value.map(item => ({
                ...item.organizationData,
                userAccessLevel: item.accessLevel // We keep the role for faster loading later
            }));
            allFoundOrgs = [...allFoundOrgs, ...emailOrgs];
        } else {
            console.warn("⚠️ [Orgs Init] Platform Email API empty or failed:", apiResponse.reason);
        }

        // 4. Deduplicate (Users might be in both APIs) and Filter DEACTIVATED
        const uniqueOrgsMap = new Map();
        allFoundOrgs.forEach(org => {
            if (org && org._id) {
                const status = String(org.status || '').toUpperCase();
                if (status !== 'DEACTIVATED' && !uniqueOrgsMap.has(org._id)) {
                    uniqueOrgsMap.set(org._id, org);
                }
            }
        });

        this.myOrgs = Array.from(uniqueOrgsMap.values());
        console.log("🎯 [Orgs Init] Final Consolidated List:", this.myOrgs);

        // 5. Existing Selection Logic
        const savedData = localStorage.getItem('preferred_org_selection');
        let orgToSelect = null;

        if (savedData) {
            const { id, timestamp } = JSON.parse(savedData);
            const twoMinutes = 2 * 60 * 1000;
            const isWithinTimeLimit = (Date.now() - timestamp) < twoMinutes;

            if (isWithinTimeLimit) {
                orgToSelect = this.myOrgs.find(o => o._id === id);
                if (orgToSelect) console.log("💾 [Orgs Init] Restoring previous selection:", orgToSelect.name);
            }
        }

        if (orgToSelect) {
            this.selectOrganization(orgToSelect);
        } else if (this.myOrgs.length > 0) {
            console.log("🖱️ [Orgs Init] Auto-selecting first organization:", this.myOrgs[0].name);
            this.selectOrganization(this.myOrgs[0]);
        }
        
    } catch (error) {
        console.error("❌ [Orgs Init] Critical Error:", error);
    }
},

        // --- UPDATED: Make this async so it waits for the config to load ---
        async selectOrganization(org) {
            this.orgData = org;

            // Fast-track access level if it came from the Platform API
            if (org.platformAccessLevel) {
                this.userRole = this.getAccessLevelKey(org.platformAccessLevel) || 1;
                this.isUserView = (this.userRole == 0 || this.userRole == 1);
            }

            await this.fetchOrgConfig(org); 
            this.accessLevel = this.getOrgAccessConfig(org._id);
            
            this.fetchOrgEmps(org._id).then(res => { 
                if (res === true) this.setKVDRState(org._id);
            });
        },

        // --- UPDATED: Uses dynamic config first ---
        getOrgAccessConfig(orgId) {
            if (this.dynamicKvdrConfig && this.dynamicKvdrConfig.accessLevel) {
                return this.dynamicKvdrConfig.accessLevel;
            }
            return kvdrOrgConfig(orgId).accessLevel;
        },

        renderChart(data) {
            const safeId = (val) => (val ? String(val) : null);
            const currentUserId = safeId(this.userId);
            
            this.isUserView = (this.userRole === '0' || this.userRole === 0 || this.userRole === '1' || this.userRole === 1);
            
            this.chartObject = new OrganiGramChart('abc123def456', 'KVDR');
            this.chartObject.container('organisationChartBody')
                .id(this.kvdrId)
                .root({
                    id: this.orgData._id,
                    name: this.orgData.name,
                    image: constants.s3Bucket + '/organization/logo/' + this.orgData.logo
                })
                .type('F')
                .data(data)
                .role(0)
                .candidateList(this.candidateList)
                .user(this.userData)
                .skills(skills)
                .softSkills(softSkills)
                .displaySettings({
                    cardColor: this.orgData.primaryThemeColor,
                    textColor: '#' + this.orgData.secondaryThemeColor,
                    borderColor: 'rgba(250,250,0,0.1)'
                })
                .onNodeClick((d) => { 
                    return this.$store.dispatch('kvdr/createKvdrRole', d)
                        .then(res => res.data || res)
                        .catch(err => {
                            this.$vs.notify({ title: 'Error', text: 'Failed to request role', color: 'danger' });
                            throw err;
                        });
                }) 
                .onNodeFetch(d => {
                    if (this.isUserView) {
                        const isUserInNode = d.kvdrRoleProfiles && d.kvdrRoleProfiles.some(p => safeId(p.userId) === currentUserId);
                        if (!isUserInNode) {
                            return Promise.resolve({
                                _id: d._id || '', title: d.title || d.name || 'Role', name: d.name || d.title || 'Role',
                                kvdrRoleProfiles: [], functionalSkills: [], behavioralSkills: [],
                            });
                        }
                    }
                    return this.$store.dispatch('kvdr/findKvdrRole', d).then(res => ({...res.data, ...res}));
                })
                .onNodeProfileFetch(d => {
                    if (this.isUserView) {
                        let requestUserId = null;
                        if (d.userId) requestUserId = safeId(d.userId);
                        else if (typeof d === 'string' && d.includes('userId=')) {
                            const match = d.match(/userId=([^&]+)/);
                            if (match) requestUserId = match[1];
                        }
                        if (!requestUserId || requestUserId !== currentUserId) return Promise.resolve([]);
                    }
                    return this.$store.dispatch('kvdr/findKvdrRoleProfile', d).then(res => res.data || []).catch(() => []);
                })
                .onNodeProfileUpdate(d => {
                    return this.$store.dispatch('kvdr/patchKvdrRoleProfile', d).then(res => {
                            this.$vs.notify({ title: 'Profile Update', text: 'Profile updated successfully', color: '#008000' });
                            return Array.isArray(res) ? res[0] : res;
                        }).catch(err => {
                            this.$vs.notify({ title: 'Error', text: 'Failed to update profile', color: 'danger' });
                            throw err;
                        });
                })
                .render();

            if (this.chartObject && typeof this.chartObject.expandRole === 'function') {
                const originalExpandRole = this.chartObject.expandRole; 

                this.chartObject.expandRole = (data, type, id) => {
                    if (this.isUserView) {
                        let targetUserId = null;
                        if (type === 'Talent' && id && id.length > 0) {
                            targetUserId = String(id[0]);
                        } 
                        else if (type === 'ROLE' && data && data.kvdrRoleId) {
                            const node = document.querySelector(`[data-empid='${data.kvdrRoleId}']`);
                            if (node && node.dataset.selectedUsr) {
                                const users = node.dataset.selectedUsr.split(",").filter(Boolean);
                                if (users.length > 0) targetUserId = String(users[0]);
                            }
                        }

                        if (targetUserId && targetUserId !== currentUserId) {
                            const chartUser = (this.candidateList || []).find(c => String(c.id) === targetUserId) || {};
                            this.openInfographicProfile(targetUserId, chartUser);
                            return; 
                        }
                    }
                    return originalExpandRole.call(this.chartObject, data, type, id);
                };
            }
        },

        openInfographicProfile(userId, userData) {
            this.selectedOtherUserId = userId;
            this.selectedOtherUserData = userData;
            this.$nextTick(() => {
                if (this.$refs.otherUserCard) this.$refs.otherUserCard.openTalentCard();
            });
        },

        createData(chartData) {
            let allRoles = chartData.kvdrRoles || [];
            const safeId = (val) => (val ? String(val) : null);
            const currentUserId = safeId(this.userId);

            if (this.isUserView) {
                const userNodes = allRoles.filter(role => role.kvdrRoleProfiles && role.kvdrRoleProfiles.some(p => safeId(p.userId) === currentUserId));
                if (userNodes.length > 0) {
                    const visibleNodeIds = new Set();
                    const getNodeId = (node) => safeId(node.selfId || node._id);
                    const getParentId = (node) => safeId(node.parentId);
                    const getAncestors = (nodeId) => {
                        if (!nodeId) return;
                        visibleNodeIds.add(nodeId); 
                        const currentNode = allRoles.find(r => getNodeId(r) === nodeId);
                        if (currentNode) {
                            const pId = getParentId(currentNode);
                            if (pId && !visibleNodeIds.has(pId) && pId !== "0") getAncestors(pId);
                        }
                    };
                    const getDescendants = (nodeId) => {
                        if (!nodeId) return;
                        visibleNodeIds.add(nodeId);
                        const children = allRoles.filter(r => getParentId(r) === nodeId);
                        children.forEach(child => {
                            const childId = getNodeId(child);
                            if (childId && !visibleNodeIds.has(childId)) getDescendants(childId);
                        });
                    };
                    userNodes.forEach(uNode => {
                        const uId = getNodeId(uNode);
                        if (uId) { getAncestors(uId); getDescendants(uId); }
                    });
                    allRoles = allRoles.filter(role => visibleNodeIds.has(getNodeId(role)));
                } else {
                    allRoles = [];
                }
            }

            const padTitle = (title) => {
                const minLength = 40; 
                if (!title) return "";
                let str = String(title);
                if (str.length >= minLength) return str;
                const diff = minLength - str.length;
                const leftPad = Math.floor(diff / 2);
                const rightPad = Math.ceil(diff / 2);
                return '\u2003'.repeat(leftPad) + str + '\u2003'.repeat(rightPad);
            };

            const graphData = allRoles.map(role => {
                let modifiedRole = { ...role };
                modifiedRole.name = padTitle(role.title); 
                modifiedRole.description = role.desc || '';
                modifiedRole.isCurrentUserNode = (role.kvdrRoleProfiles || []).some(p => safeId(p.userId) === currentUserId);
                modifiedRole.profiles = (role.kvdrRoleProfiles || []).map(p => {
                    let assessmentData = { 'Fitment (Fit)': [], 'Performance (Drive)': [], 'Succession (Secure)': [], 'Appraisal': [] };
                    const isCurrentUser = safeId(p.userId) === currentUserId;
                    if (!this.isUserView || isCurrentUser) {
                        assessmentData = {
                            'Fitment (Fit)': p.fitmentData || [],
                            'Performance (Drive)': p.performanceData || [],
                            'Succession (Secure)': p.successionData || [],
                            'Appraisal': p.appraisalData || []
                        };
                    }
                    return { userId: p.userId, assessmentData: assessmentData };
                });
                return modifiedRole;
            });

            return {
                id: chartData._id, 
                name: chartData.title, 
                version: chartData.version || "",
                // --- UPDATED: Pass the dynamic config here instead of the hardcoded one! ---
                kvdrOrgConfigData: this.dynamicKvdrConfig || kvdrOrgConfig(this.orgData._id), 
                graphData: graphData
            };
        },

        setupCandidateList() {
            if (this.orgEmployees.length) {
                this.candidateList = this.employeesData.map(m =>
                    ({
                        id: m.userId, name: m.userData.firstName + ' ' + m.userData.lastName,
                        imageUrl: (m.userData.profilePictureUrl) ? `${constants.s3Bucket}/editor/profile-pictures/${m.userData.profilePictureUrl}` : `https://hostedboringavatars.vercel.app/api/beam?name=${m.userData.firstName}`,
                        emailId: m.userData.email,
                    }))
            } else {
                this.candidateList = [{
                    id: this.userId, name: this.userData.firstName + ' ' + this.userData.lastName,
                    imageUrl: (this.userData.profilePictureUrl) ? `${constants.s3Bucket}/editor/profile-pictures/${this.userData.profilePictureUrl}` : `https://hostedboringavatars.vercel.app/api/beam?name=${this.userData.firstName}`,
                    emailId: this.userData.email,
                }]
            }
        },

        setKVDRState(_id) {
            this.$store.dispatch("kvdr/findKVDR", _id).then(res => {
                    if(!res.data.length) return;
                    this.kvdrId = res.data[0]._id;
                    this.setupCandidateList();
                    this.dataCollection = this.createData(res.data[0]);
                    this.renderChart(this.dataCollection);
                }).catch((err) => { console.error('Error setting KVDR state:', err); });
        },

        async fetchOrgEmps(orgId) {
            return this.$store.dispatch("organization/fetchAllEmployees", { organizationId: orgId })
                .then(employees => {
                    this.orgEmployees = employees;
                    const userData = employees.find(e => this.userId === e.userId);
                    if (userData) {
                        this.userRole = this.getAccessLevelKey(userData.accessLevel) || 1;
                        this.isUserView = (this.userRole == 0 || this.userRole == 1);
                    }
                    return true;
                }).catch(err => { console.error(err); return false; })
        },

        getAccessLevelKey(label) {
            return this.accessLevel.find(item => item.label === label)?.key || 0;
        },

        closeDropdown(e) {
            if (!this.$el.contains(e.target)) {
                this.isDropdownOpen = false;
            }
        }
    },
    mounted() { 
        this.initializeOrgs(); 
        document.addEventListener('click', this.closeDropdown);
    },
    beforeDestroy() {
        document.removeEventListener('click', this.closeDropdown);
    }
}
</script>

<style scoped>
.dropdown-container {
    position: relative;
    z-index: 1000;
}

/* 2. Ensure the chart body stays at a lower stacking level */
#organisationChartBody { 
    border-top: 1px solid #f1f5f9; 
    min-height: 600px;
    position: relative;
    z-index: 1; /* Keep chart layer low */
}

/* 3. Remove any overflow restrictions on parent containers 
   that might be clipping the dropdown shadow or menu */
.relative.z-\[100\] {
    overflow: visible !important;
}
/* Custom animations and styling for the professional dropdown */
.org-dropdown-trigger {
    outline: none !important;
}

.org-dropdown-trigger:hover {
    border-color: #3182ce;
    background-color: #f7fafc;
}

.rotate-180 {
    transform: rotate(180deg);
}

/* Transition for dropdown */
.fade-slide-enter-active, .fade-slide-leave-active {
    transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}
.fade-slide-enter, .fade-slide-leave-to {
    opacity: 0;
    transform: translateY(-10px);
}

/* Scrollbar for dropdown */
::-webkit-scrollbar {
    width: 6px;
}
::-webkit-scrollbar-track {
    background: #f1f1f1;
}
::-webkit-scrollbar-thumb {
    background: #cbd5e0;
    border-radius: 10px;
}
::-webkit-scrollbar-thumb:hover {
    background: #a0aec0;
}
.org-dropdown-menu{
    width: 280px;
    background: white;
    border-radius: 14px;
    box-shadow: 0 10px 30px rgba(0,0,0,0.08);
    border: 1px solid #f1f1f1;
    overflow: hidden;
}

/* Org item row */
.org-item{
    display:flex;
    align-items:center;
    gap:12px;
    padding:12px 16px;
    cursor:pointer;
    text-align:left;
    transition:all .2s ease;
}

/* Left align text */
.org-item span{
    flex:1;
    text-align:left;
    font-size:14px;
    font-weight:500;
    color:#444;
}

/* Yellow-orange hover */
.org-item:hover{
    background:#FFB200;
}

.org-item:hover span{
    color:white;
}

/* Circle count */
.org-count-circle{
    background:#FFB200;
    color:white;
    width:22px;
    height:22px;
    display:flex;
    align-items:center;
    justify-content:center;
    border-radius:50%;
    font-size:11px;
    font-weight:600;
}

/* Base styles for chart */
#organisationChartBody { border-top: 1px solid #f1f5f9; min-height: 600px;}
::v-deep .node rect { rx: 5px; ry: 5px; }
::v-deep .selectedUsrImg { flex-shrink: 0 !important; min-width: 32px !important; min-height: 32px !important; }
::v-deep image { object-fit: cover !important; }
::v-deep .node foreignObject { overflow: visible; }
::v-deep .node foreignObject > div { min-width: 280px !important; width: 280px !important; min-height: 90px !important; display: flex !important; justify-content: center; align-items: center; box-sizing: border-box; }
::v-deep text { white-space: pre !important; }
::v-deep .node-add-btn, ::v-deep .org-add-button, ::v-deep g[style*="cursor: pointer"] circle, ::v-deep .node-button-g { display: none !important; pointer-events: none !important; opacity: 0 !important; }
::v-deep .read-only-chart .member { cursor: pointer !important; }
@import '../../../org-dashboard/users/components/OrganiGram/public/css/app.css';
</style>